import { CompanyFilterBaseDataProvider } from './CompanyFilterBaseDataProvider';
import { GetTargetsRequest, GetTargetsResponse } from '@nirvana/shared/dtos/types/alerts/gz-console';
import { GetCompaniesRequest, GetCompaniesResponse } from '@nirvana/shared/dtos/types/protected-entities/gz-console';
import { ConsoleRequest, DataProviderType } from 'apps/webcomponents/src/utils/request';
import { CompanyEndpointMethod } from '../../../quarantine/exchangeProtection/types';
import { Target } from '@nirvana/shared/company-helper/nestjs';
import { MAX_COMPANY_LIMIT } from '../types/types';

export class CompanyFilterEdrDataProvider extends CompanyFilterBaseDataProvider {
  private async getRequiredTargets(request: GetTargetsRequest): Promise<Array<Target>> {
    const selectedCompanyIds = request.params?.companyIds ?? [];

    if (!selectedCompanyIds.length) {
      return [];
    }

    let requestLimit = MAX_COMPANY_LIMIT;
    let ignoreLimit = false;

    // If the user has selected more than 100 companies in the selector, keep only the first 100 selected
    if (selectedCompanyIds.length > MAX_COMPANY_LIMIT) {
      requestLimit = 100;

      // Bypass the 25 companies max limit
      ignoreLimit = true;
    }

    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: 'Company',
      method: CompanyEndpointMethod.GET_AVAILABLE_COMPANIES,
      payload: {
        // by default, page is set to 1 for this request
        limit: requestLimit,
        options: {
          source: 'fromIncidents',
          companyIds: selectedCompanyIds,
          ignoreLimit: ignoreLimit,
          countRecords: false,
        },
      },
    });

    return response.data.map(({ _id, name }) => ({ companyId: _id, companyName: name }));
  }

  private async getInitialTargetsResponse(): Promise<GetCompaniesResponse> {
    try {
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.ENDPOINTS,
        endpoint: 'Company',
        method: CompanyEndpointMethod.GET_AVAILABLE_COMPANIES,
        payload: {
          // by default, page is set to 1 for this request
          limit: 10,
          options: {
            source: 'fromIncidents',
            countRecords: false,
          },
        },
      });

      return {
        targets: response.data.map(({ _id, name }) => ({ companyId: _id, companyName: name })),
        totalCount: response.count,
      };
    } catch (e) {
      throw new Error(e);
    }
  }

  async getInitialTargets(request: GetTargetsRequest): Promise<GetTargetsResponse | undefined> {
    try {
      const [requiredTargets, responseInitialTargets] = await Promise.all([
        this.getRequiredTargets(request),
        this.getInitialTargetsResponse(),
      ]);

      const targets = [...requiredTargets, ...responseInitialTargets.targets];

      // It is possible that some of the selected companies have been fetched twice, as being in the first page
      const uniqueTargets = [...new Map(targets.map(target => [target.companyId, target])).values()];

      return {
        defaultTarget: [],
        targets: uniqueTargets,
        totalCount: responseInitialTargets.totalCount,
      };
    } catch (e) {
      throw new Error(e);
    }
  }

  async getCompanies({ companyName, page, limit }: GetCompaniesRequest): Promise<GetCompaniesResponse | undefined> {
    try {
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.ENDPOINTS,
        endpoint: 'Company',
        method: CompanyEndpointMethod.GET_AVAILABLE_COMPANIES,
        payload: {
          options: {
            source: 'fromIncidents',
            countRecords: false,
          },
          companyName,
          limit,
          page,
        },
      });

      return {
        targets: response.data.map(({ _id, name }) => ({ companyId: _id, companyName: name })),
        totalCount: response.count,
      };
    } catch (e) {
      throw new Error(e);
    }
  }
}
